import React from 'react';
import { Chip as MuiChip, ChipProps as MuiChipProps, SxProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { CHIP_RADIUS_PX, CHIP_AVATAR_INSET_PX } from './constants';

// Stable identity, used when `disabled` to keep MUI Chip rendering the
// delete-icon slot (which it omits when `onDelete` is undefined).
const NOOP = () => {};

/** Pixel geometry the chip parent computes from its size / variant. */
export interface BaseChipGeometry {
  height: number;
  /** Horizontal padding (left + right), px. */
  paddingX: number;
  gap: number;
  /** Size for every `<Icon>` in the chip, applied via `& .MuiSvgIcon-root`. */
  iconPx: number;
  /** Avatar diameter; defaults to `height - CHIP_AVATAR_INSET_PX`. */
  avatarPx?: number;
}

export interface BaseChipProps
  extends
    Omit<
      MuiChipProps,
      | 'label'
      | 'variant'
      | 'size'
      | 'color'
      | 'icon'
      | 'deleteIcon'
      | 'onDelete'
      | 'clickable'
      | 'onClick'
      | 'sx'
    >,
    PounceStyleProps {
  sx?: SxProps;
  /** Pre-composed MUI variant string (e.g. `static-filled-primary`). */
  muiVariant: string;
  muiSize: 'lg' | 'md' | 'sm';
  geometry: BaseChipGeometry;
  /** Single flex row of every visible piece (lead → field → label → counter → chevron). */
  label: React.ReactNode;
  /** Goes into MUI's delete slot; MUI binds Backspace/Delete + click here. */
  deleteIcon?: React.ReactElement;
  /** Passing a handler is also the "this chip is interactive" signal — `clickable` is derived. */
  onClick?: (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
  onDelete?: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  ariaProps?: React.AriaAttributes & { role?: React.AriaRole };
  rootDataAttribute: 'data-pv2-chip-static' | 'data-pv2-chip-interactive';
}

/**
 * Internal shared base for the chip family. Not exported from the package
 * barrel — `StaticChip` and `InteractiveChip` are thin configurations.
 */
export const BaseChip = React.forwardRef<HTMLDivElement, BaseChipProps>(function BaseChip(
  {
    muiVariant,
    muiSize,
    geometry,
    label,
    deleteIcon,
    onClick,
    onDelete,
    disabled = false,
    tabIndex,
    ariaProps,
    rootDataAttribute,
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  const clickable = onClick != null && !disabled;
  const avatarPx = geometry.avatarPx ?? geometry.height - CHIP_AVATAR_INSET_PX;

  // When disabled, swap onDelete to NOOP rather than undefined — MUI drops the
  // delete-icon slot if onDelete is undefined, which would visually erase the
  // × glyph from a disabled chip instead of just suppressing the action.
  let safeOnDelete: BaseChipProps['onDelete'];
  if (onDelete !== undefined) {
    safeOnDelete = disabled ? NOOP : onDelete;
  }

  const sizeSx: SxProps = {
    height: geometry.height,
    px: `${geometry.paddingX}px`,
    borderRadius: `${CHIP_RADIUS_PX}px`,
    '--chip-avatar-size': `${avatarPx}px`,
    // Descendant selector beats pounceV2 `<Icon>`'s own emotion class on
    // specificity, so consumer `size` props on icons are overridden. Same
    // pattern MuiButton/MuiAlert/MuiAutocomplete themes use.
    '& .MuiSvgIcon-root': {
      width: geometry.iconPx,
      height: geometry.iconPx,
      fontSize: geometry.iconPx,
    },
    // Lead/label/trail/counter/chevron share this single flex row so spacing
    // is uniform across slots. (MUI's `.MuiChip-icon` slot is intentionally
    // unused — it sits outside this row, which would produce uneven spacing.)
    '& .MuiChip-label': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: `${geometry.gap}px`,
      padding: 0,
    },
    '& .MuiChip-deleteIcon': {
      margin: 0,
      marginLeft: `${geometry.gap}px`,
    },
    ...(clickable ? { '&:hover, &:focus-visible': { cursor: 'pointer' } } : {}),
  };

  return (
    <MuiChip
      ref={ref}
      variant={muiVariant as never}
      size={muiSize as never}
      disabled={disabled}
      clickable={clickable}
      aria-disabled={disabled || undefined}
      label={label}
      deleteIcon={deleteIcon}
      // React-handler gate (not MUI's CSS `pointer-events: none`) — JSDOM and
      // synthetic-event paths in tests bypass pointer-events.
      onClick={clickable ? onClick : undefined}
      onDelete={safeOnDelete}
      tabIndex={tabIndex}
      {...ariaProps}
      {...{ [rootDataAttribute]: '' }}
      sx={{ ...pounceStyles, ...sizeSx, ...sx } as SxProps}
      {...otherProps}
    />
  );
});
